/* utils/storage.js --------------------------------------------------------- */

const LS_KEY_ARCHIVE = "cogneetiv.archive.v1";
const LS_KEY_BRAIN   = "cogneetiv.neural.v1";

/* ----------------------- Shared helpers ----------------------- */
function nowISO() {
  return new Date().toISOString();
}
function uid(prefix = "c") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
function dedupeStrings(arr) {
  return Array.from(new Set((arr || []).map(x => String(x).trim()).filter(Boolean)));
}

/* ----------------------- Archive helpers ---------------------- */
function loadAllArchive() {
  try {
    const raw = localStorage.getItem(LS_KEY_ARCHIVE);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    // bad JSON / storage issue
    return [];
  }
}
function saveAllArchive(arr) {
  localStorage.setItem(LS_KEY_ARCHIVE, JSON.stringify(arr));
  window.dispatchEvent(new CustomEvent("archive:changed"));
}
function basicKeywords(text = "", limit = 12) {
  const stop = new Set([
    "the","and","with","that","this","from","your","into","over","under",
    "for","you","are","was","were","will","can","could","should","would",
    "have","has","had","about","than","then","also","such","like","when",
    "what","how","why","where","which","who","while","to","in","on","of",
    "a","an","is","it","i","my","me","we","our","us"
  ]);
  const words = (text.toLowerCase().match(/[a-z](?:[a-z-]{2,})/g) || []).filter(w => !stop.has(w));
  const freq = {};
  for (const w of words) freq[w] = (freq[w] || 0) + 1;
  return Object.keys(freq).sort((a,b) => freq[b] - freq[a]).slice(0, limit);
}
function unique(arr) { return Array.from(new Set(arr.filter(Boolean))); }

function deriveTitle(mode, userText) {
  const head = (userText || "").trim();
  if (!head) return (mode ? (mode[0].toUpperCase() + mode.slice(1)) : "Conversation");
  return (mode ? mode.toUpperCase() + ": " : "") + (head.length > 60 ? head.slice(0, 59) + "…" : head);
}
function autoSummary(messages = []) {
  const s = (messages || []).map(m => `${m.role}: ${m.content || ""}`).join(" ");
  return s.length > 220 ? s.slice(0, 219) + "…" : s || "No content";
}
export function normalizeConversation(c) {
  if (!c || typeof c !== "object") return null;
  const messages = Array.isArray(c.messages) ? c.messages.map(m => ({
    role: m && m.role || "user",
    content: (m && m.content) ? String(m.content) : ""
  })) : [];
  return {
    id: c.id || uid("c"),
    mode: c.mode || "chat",
    title: c.title || deriveTitle(c.mode || "chat", (messages[0] && messages[0].role === "user" && messages[0].content) || ""),
    summary: c.summary || autoSummary(messages),
    keywords: Array.isArray(c.keywords) ? dedupeStrings(c.keywords) : [],
    createdAt: c.createdAt || nowISO(),
    updatedAt: c.updatedAt || nowISO(),
    messages,
  };
}

/* ------------------------- ArchiveAPI ------------------------- */
export const ArchiveAPI = {
  /**
   * Create a new conversation.
   * @param {object} opts {mode, userText, aiText, keywords?}
   * @returns conversation object
   */
  create(opts = {}) {
    const { mode = "chat", userText = "", aiText = "", keywords = [] } = opts;

    const messages = [
      userText ? { role: "user", content: String(userText) } : null,
      aiText   ? { role: "assistant", content: String(aiText) } : null,
    ].filter(Boolean);

    const combined = [userText, aiText].filter(Boolean).join(" ");
    const kws = unique([ ...(keywords || []), ...basicKeywords(combined) ]);

    const conv = normalizeConversation({
      id: uid("c"),
      mode,
      title: deriveTitle(mode, userText),
      summary: autoSummary(messages),
      keywords: kws,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      messages,
    });

    const all = loadAllArchive();
    all.unshift(conv);            // newest first
    saveAllArchive(all);
    return conv;
  },

  /**
   * Append a single message to a conversation (by id or object).
   * Optionally pass extraKeywords to merge.
   */
  appendMessage(convOrId, message, extraKeywords = []) {
    if (!message || !message.role) return null;
    const id = typeof convOrId === "string" ? convOrId : (convOrId && convOrId.id);
    if (!id) return null;

    const all = loadAllArchive();
    const idx = all.findIndex(c => c.id === id);
    if (idx === -1) return null;

    all[idx].messages = all[idx].messages || [];
    all[idx].messages.push({ role: message.role, content: String(message.content || "") });
    all[idx].updatedAt = nowISO();

    // update title from first user msg if missing
    if (!all[idx].title) {
      const firstUser = all[idx].messages.find(m => m.role === "user");
      all[idx].title = deriveTitle(all[idx].mode, firstUser && firstUser.content);
    }

    // recompute summary lightly
    all[idx].summary = autoSummary(all[idx].messages);

    // update keywords
    const text = (message.content || "");
    const kws = unique([...(all[idx].keywords || []), ...basicKeywords(text), ...(extraKeywords || [])]);
    all[idx].keywords = kws;

    saveAllArchive(all);
    return all[idx];
  },

  list() {
    const all = loadAllArchive();
    return all
      .map(normalizeConversation)
      .sort((a,b) => +new Date(b.updatedAt || b.createdAt || 0) - +new Date(a.updatedAt || a.createdAt || 0));
  },

  get(id) {
    const c = loadAllArchive().find(c => c.id === id) || null;
    return c ? normalizeConversation(c) : null;
  },

  /**
   * Generic partial update used by your views:
   * ArchiveAPI.update(id, { title, summary, keywords, mode, messages })
   */
  update(id, patch = {}) {
    const all = loadAllArchive();
    const c = all.find(x => x.id === id);
    if (!c) return null;

    if (patch.mode != null) c.mode = String(patch.mode || "chat");
    if (patch.title != null) c.title = String(patch.title || "");
    if (patch.summary != null) c.summary = String(patch.summary || "");
    if (patch.keywords != null) c.keywords = dedupeStrings(patch.keywords);
    if (patch.messages != null && Array.isArray(patch.messages)) {
      c.messages = patch.messages.map(m => ({ role: m.role, content: String(m.content || "") }));
    }

    // keep summary sane if messages changed and summary wasn't explicitly set
    if (patch.messages && patch.summary == null) {
      c.summary = autoSummary(c.messages || []);
    }

    c.updatedAt = nowISO();
    saveAllArchive(all);
    return normalizeConversation(c);
  },

  /** Convenience helpers (still used by some views) */
  setSummary(id, summary) {
    return this.update(id, { summary });
  },
  updateTitle(id, title) {
    return this.update(id, { title });
  },
  setKeywords(id, keywords = []) {
    return this.update(id, { keywords: dedupeStrings(keywords) });
  },
  addKeywords(id, keywords = []) {
    const c = this.get(id);
    if (!c) return null;
    const next = dedupeStrings([...(c.keywords || []), ...keywords]);
    return this.update(id, { keywords: next });
  },
  removeKeyword(id, keyword) {
    const c = this.get(id);
    if (!c) return null;
    const next = (c.keywords || []).filter(k => k !== keyword);
    return this.update(id, { keywords: next });
  },

  /** Delete (with friendly aliases) */
  remove(id) {
    const all = loadAllArchive().filter(c => c.id !== id);
    saveAllArchive(all);
    return true;
  },
  delete(id) {            // alias for compatibility
    return this.remove(id);
  },
  destroy(id) {           // alias for compatibility
    return this.remove(id);
  },

  /** Bulk remove (used by bulk delete UI) */
  bulkRemove(ids = []) {
    const toDelete = new Set(ids);
    const all = loadAllArchive().filter(c => !toDelete.has(c.id));
    saveAllArchive(all);
    return true;
  },
};

/* ----------------------- Neural Brain helpers ----------------------- */
function loadAllBrain() {
  try {
    const raw = localStorage.getItem(LS_KEY_BRAIN);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}
function saveAllBrain(arr) {
  localStorage.setItem(LS_KEY_BRAIN, JSON.stringify(arr));
  window.dispatchEvent(new CustomEvent("neural:changed"));
}

/* -------------------------- NeuralAPI -------------------------- */
/**
 * A minimal append-only “knowledge base”.
 * Records are arbitrary JSON objects with an id and timestamp attached.
 * Used by the app to ingest web research and summaries.
 *
 * Example entries:
 *  { type: "web_research", query: "...", records: [{title, link, snippet}, ...] }
 *  { type: "web_summary",  query: "...", summary: "..." }
 */
export const NeuralAPI = {
  ingest(entry) {
    const all = loadAllBrain();
    const rec = {
      id: uid("kb"),
      ts: Date.now(),
      iso: nowISO(),
      ...entry
    };
    all.unshift(rec); // newest first
    saveAllBrain(all);
    return rec;
  },
  list() {
    return loadAllBrain().sort((a,b) => (b.ts || 0) - (a.ts || 0));
  },
  get(id) {
    return loadAllBrain().find(r => r.id === id) || null;
  },
  search(q = "") {
    const needle = q.trim().toLowerCase();
    if (!needle) return this.list();
    return loadAllBrain().filter(rec => JSON.stringify(rec).toLowerCase().includes(needle));
  },
  remove(id) {
    const filtered = loadAllBrain().filter(r => r.id !== id);
    saveAllBrain(filtered);
    return true;
  },
  clear() {
    saveAllBrain([]);
    return true;
  }
};

export default ArchiveAPI;
